import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { CalendarDays, X } from 'lucide-react';
import {
  format,
  startOfDay, endOfDay,
  startOfWeek, endOfWeek,
  startOfMonth, endOfMonth,
  subDays, subWeeks, subMonths, addDays,
} from 'date-fns';
import type { DateRange } from 'react-day-picker';

export interface DateRangeValue { from: Date; to: Date; }

interface Props {
  value: DateRangeValue | null;
  onChange: (v: DateRangeValue | null) => void;
  placeholder?: string;
}

const now = () => new Date();

const PRESETS: { label: string; get: () => DateRangeValue }[] = [
  { label: 'Today',      get: () => ({ from: startOfDay(now()), to: endOfDay(now()) }) },
  { label: 'Yesterday',  get: () => { const d = subDays(now(), 1); return { from: startOfDay(d), to: endOfDay(d) }; } },
  { label: 'This Week',  get: () => ({ from: startOfWeek(now(), { weekStartsOn: 0 }), to: endOfDay(now()) }) },
  { label: 'Last Week',  get: () => { const d = subWeeks(now(), 1); return { from: startOfWeek(d, { weekStartsOn: 0 }), to: endOfWeek(d, { weekStartsOn: 0 }) }; } },
  { label: 'This Month', get: () => ({ from: startOfMonth(now()), to: endOfDay(now()) }) },
  { label: 'Last Month', get: () => { const d = subMonths(now(), 1); return { from: startOfMonth(d), to: endOfMonth(d) }; } },
];

export function DateRangeFilter({ value, onChange, placeholder = 'Select Date Range' }: Props) {
  const [open, setOpen] = useState(false);
  const [calRange, setCalRange] = useState<DateRange | undefined>(
    value ? { from: value.from, to: value.to } : undefined,
  );
  const [daysUpTo, setDaysUpTo] = useState('7');
  const [daysFrom, setDaysFrom] = useState('7');
  const [activePreset, setActivePreset] = useState<string | null>(null);

  function apply(r: DateRangeValue, label?: string) {
    setCalRange({ from: r.from, to: r.to });
    onChange(r);
    setActivePreset(label ?? null);
    setOpen(false);
  }

  function handleCalendarSelect(r: DateRange | undefined) {
    setCalRange(r);
    setActivePreset(null);
    if (r?.from && r?.to) {
      onChange({ from: startOfDay(r.from), to: endOfDay(r.to) });
    } else if (!r?.from) {
      onChange(null);
    }
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    setCalRange(undefined);
    setActivePreset(null);
    onChange(null);
  }

  const btnLabel = value
    ? `${format(value.from, 'MMM d, yyyy')} – ${format(value.to, 'MMM d, yyyy')}`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`gap-2 font-normal ${value ? 'text-foreground' : 'text-muted-foreground'}`}
        >
          <CalendarDays className="h-4 w-4 shrink-0" />
          <span className="max-w-56 truncate">{btnLabel}</span>
          {value && (
            <X
              className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground shrink-0"
              onClick={clear}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex p-0 w-auto shadow-md" sideOffset={4}>
        {/* Presets sidebar */}
        <div className="flex flex-col border-r min-w-44 py-2">
          {PRESETS.map(p => (
            <button
              key={p.label}
              onClick={() => apply(p.get(), p.label)}
              className={`text-left text-sm px-4 py-2 transition-colors hover:bg-accent ${
                activePreset === p.label ? 'text-primary font-medium bg-accent/60' : 'text-foreground'
              }`}
            >
              {p.label}
            </button>
          ))}

          <div className="px-3 mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                value={daysUpTo}
                onChange={e => setDaysUpTo(e.target.value)}
                className="h-7 w-12 text-center px-1 text-sm"
              />
              <button
                onClick={() => {
                  const n = Math.max(1, parseInt(daysUpTo) || 1);
                  apply({ from: startOfDay(subDays(now(), n - 1)), to: endOfDay(now()) }, `${n}d↑`);
                }}
                className="text-xs text-muted-foreground hover:text-foreground whitespace-nowrap transition-colors text-left"
              >
                days up to today
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                value={daysFrom}
                onChange={e => setDaysFrom(e.target.value)}
                className="h-7 w-12 text-center px-1 text-sm"
              />
              <button
                onClick={() => {
                  const n = Math.max(1, parseInt(daysFrom) || 1);
                  apply({ from: startOfDay(now()), to: endOfDay(addDays(now(), n - 1)) }, `${n}d→`);
                }}
                className="text-xs text-muted-foreground hover:text-foreground whitespace-nowrap transition-colors text-left"
              >
                days starting today
              </button>
            </div>
          </div>
        </div>

        {/* Calendar panel */}
        <div className="p-3">
          <div className="flex gap-2 mb-3">
            <div className="flex-1 border rounded px-3 py-1.5 text-sm text-center min-w-32 bg-muted/30">
              {calRange?.from
                ? format(calRange.from, 'MMM d, yyyy')
                : <span className="text-muted-foreground">Start date</span>}
            </div>
            <div className="flex-1 border rounded px-3 py-1.5 text-sm text-center min-w-32 bg-muted/30">
              {calRange?.to
                ? format(calRange.to, 'MMM d, yyyy')
                : <span className="text-muted-foreground">End date</span>}
            </div>
          </div>
          <Calendar
            mode="range"
            selected={calRange}
            onSelect={handleCalendarSelect}
            numberOfMonths={1}
            defaultMonth={calRange?.from ?? now()}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
